import React, { useState, useEffect } from 'react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { MetricCard } from '../shared/MetricCard'
import { Trophy, HelpCircle, Users, Waves, Timer, ChevronRight } from 'lucide-react'
import { formatCurrency, formatUnderscoreCase, getSurferRating } from '../../utils'
import { Tooltip, Select, SelectItem } from '@nextui-org/react'
import LOCATIONS from '@/constants/locations'
import { Toaster, toast } from 'sonner'
import {
  generateEnrollData,
  getStartHeatData,
  getOrdinalSuffix,
  getLeaderboard,
  calculatePrize
} from '@/engine/competition'
import { Competitor, CompetitionRound, CompetitionEnum } from '@/types'
import { RoundsDisplay } from '../shared/RoundsDisplay'
import { toastHeatVictory, toastHeatElimination, toastCompetitionVictory } from '../shared/CommonToasts'
import { Tabs } from '../shared/Tabs'

interface CompetitionRoundsViewProps {
  rounds: CompetitionRound[]
  currentRoundIndex: number
  currentHeatIndex: number
  isComplete?: boolean
}

const CompetitionRoundsView: React.FC<CompetitionRoundsViewProps> = ({
  rounds,
  currentRoundIndex,
  currentHeatIndex,
  isComplete
}) => {
  return (
    <div>
      <h4 className="text-lg font-semibold mb-4">{isComplete ? 'Competition History' : 'Competition Progress'}</h4>
      <RoundsDisplay
        rounds={rounds}
        currentRoundIndex={isComplete ? rounds.length - 1 : currentRoundIndex}
        currentHeatIndex={isComplete ? rounds[rounds.length - 1].heats.length - 1 : currentHeatIndex}
      />
    </div>
  )
}

const getRatingComparisonColor = (playerRating: number, competitorRating: number): string => {
  const ratio = playerRating / competitorRating
  if (ratio >= 0.9 && ratio <= 1.1) {
    return 'text-orange-400' // Similar ratings
  }
  return ratio > 1.1 ? 'text-green-400' : 'text-red-400' // Better or worse
}

interface CompetitionCardProps {
  competition: any // Replace with your competition type
  surferRating: number
  onEnroll: (competitionId: CompetitionEnum) => void
  disabled: boolean
}

const CompetitionCard: React.FC<CompetitionCardProps> = ({ competition, surferRating, onEnroll, disabled }) => {
  const { gameData } = useGameplayContext()
  const competitionStats = gameData.competitionStats[competition.key as CompetitionEnum]
  const lastPlayedDay = competitionStats?.lastCompetitionGameDay || 0

  let enrollButtonText = `Enroll ($${formatCurrency(competition.entryFee)})`
  let isDisabled = disabled

  // If competition has been played before
  if (lastPlayedDay > 0) {
    if (competition.restartIntervalDays === undefined) {
      enrollButtonText = 'Competition Closed'
      isDisabled = true
    } else {
      const currentDay = Math.floor(gameData.gameMinutes / (60 * 24))
      const daysSinceLastPlay = currentDay - lastPlayedDay
      if (daysSinceLastPlay < competition.restartIntervalDays) {
        const daysRemaining = competition.restartIntervalDays - daysSinceLastPlay
        enrollButtonText = `Available in ${daysRemaining} days`
        isDisabled = true
      }
    }
  }

  return (
    <div className="bg-gradient-to-br from-white to-blue-100 rounded-lg p-4 shadow-lg">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-blue-900">{competition.name}</h3>
      </div>

      <div className="flex items-center text-sm  mb-4">
        <span className="text-2xl mr-2">⭐</span>
        <div className="flex gap-4">
          <span className="text-blue-900">
            Competitor Rating: <span className="font-bold"> {competition.avgCompetitorRating}</span>
          </span>
          <span className={getRatingComparisonColor(surferRating, competition.avgCompetitorRating)}>
            You: <span className="font-bold"> {surferRating}</span>
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">🏆</span>
        <div className="grid grid-cols-3 gap-2 flex-1">
          <div className="bg-yellow-500/20 p-2 rounded">
            <div className="text-xs text-blue-900">1st</div>
            <div className="text-sm font-semibold text-blue-700">
              ${formatCurrency(calculatePrize(competition.prize, 1))}
            </div>
          </div>
          <div className="bg-gray-400/20 p-2 rounded">
            <div className="text-xs text-blue-900">2nd</div>
            <div className="text-sm font-semibold text-blue-700">
              ${formatCurrency(calculatePrize(competition.prize, 2))}
            </div>
          </div>
          <div className="bg-amber-700/20 p-2 rounded">
            <div className="text-xs text-blue-900">3rd</div>
            <div className="text-sm font-semibold text-blue-700">
              ${formatCurrency(calculatePrize(competition.prize, 3))}
            </div>
          </div>
        </div>
      </div>

      <button
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-3 rounded w-full disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => onEnroll(competition.key as CompetitionEnum)}
        disabled={isDisabled}
      >
        {enrollButtonText}
      </button>
    </div>
  )
}

const tooltipContent = (
  <div className="max-w-md space-y-1 p-2">
    <p className="pt-2 pb-2 border-b border-gray-300">
      Compete against other surfers in organized competitions! Each competition shows:
      <br />⭐ Competitor Rating vs Your Rating. Color coding shows your chances:
      <br />•<span className="text-green-400 ml-4">Green</span> - You're favored to win
      <br />•<span className="text-orange-400 ml-4">Orange</span> - Even match
      <br />•<span className="text-red-400 ml-4">Red</span> - Tough competition
    </p>

    <p className="pt-2 pb-2 border-b border-gray-300">
      🏆 Prize distribution for each competition:
      <br />• 1st Place: 60% of prize pool
      <br />• 2nd Place: 30% of prize pool
      <br />• 3rd Place: 2x 10% of prize pool
    </p>

    <p className="pt-2 pb-2 border-b border-gray-300">
      💰 Entry fee varies by competition level. Higher entry fees usually mean bigger prize pools and tougher
      competition!
    </p>

    <p className="pt-2 pb-2 border-b border-gray-300">
      🌊 After enrolling, you'll compete in multiple heats:
      <br />• Each heat requires you to surf against other competitors
      <br />• In numbered rounds, top 2 surfers out of 4 advance to the next round, 3rd and 4th are eliminated
      <br />• In quarterfinals and later, only 1 surfer out of 2 advances to next round
    </p>

    <p className="pt-2 pb-2">
      📊 After the competition ends, you can view the complete leaderboard with prizes and positions
    </p>
  </div>
)

export const CompetitionScreen: React.FC = () => {
  const { gameData, setGameData } = useGameplayContext()
  const { currentCompetition: gdcc } = gameData
  const itemsPerPage = 10
  const currentLocation = LOCATIONS[gameData.currentLocation]
  const [resultsTab, setResultsTab] = useState('leaderboard')

  const currentRound = gdcc?.rounds[gdcc.currentRoundIndex]
  const currentHeat = currentRound?.heats[gdcc?.currentHeatIndex ?? 0]
  const isPlayerInHeat = currentHeat?.competitors.some((name) => name === gameData.username) ?? false

  const competitorMap = gdcc?.competitorMap ?? {}
  const leaderboard = getLeaderboard(competitorMap)
  const handleEnroll = (competitionId: CompetitionEnum) => {
    const newData = generateEnrollData(gameData, competitionId)
    setGameData(newData)
  }

  const handleStartHeat = () => {
    const newData = getStartHeatData(gameData)
    const { currentCompetition } = newData
    if (!currentCompetition) return
    const playerResult = currentCompetition.competitorMap[newData.username]
    const currentRoundIndex = currentCompetition.currentRoundIndex ?? 0
    const previousRoundIndex = currentCompetition.isComplete ? currentRoundIndex : Math.max(0, currentRoundIndex - 1)
    const previousRound = currentCompetition.rounds[previousRoundIndex]
    const playerRoundKey = newData.currentCompetition?.rounds[previousRoundIndex]?.key
    if (playerResult && previousRound) {
      if (playerResult.eliminatedInRound && playerResult.position !== 1) {
        // Player was eliminated in this heat
        toastHeatElimination(currentRound?.key ?? '', playerResult.eliminatedInHeat ?? 0)
      } else {
        // Player advanced - find their heat result
        const heatResult = currentHeat?.results?.find((r) => r.competitorName === newData.username)
        if (heatResult) {
          if (playerRoundKey === 'finals' && heatResult.position === 1) {
            // Won the entire competition
            toastCompetitionVictory(playerResult.prize || 0)
          } else {
            toastHeatVictory(
              playerRoundKey ?? '',
              (newData.currentCompetition?.currentHeatIndex ?? 0) + 1,
              heatResult.score,
              heatResult.position
            )
          }
        }
      }
    }

    setGameData(newData)
  }

  const handleClose = () => {
    setGameData((prev) => ({
      ...prev,
      currentCompetition: undefined
    }))
  }

  const currentCompetition = gdcc ? currentLocation.competitions?.find((c) => c.key === gdcc?.key) : null
  const surferRating = getSurferRating(gameData)
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h2 className="text-3xl font-bold text-white drop-shadow-md">🏆 Competitions</h2>
        <Tooltip content={tooltipContent} className="bg-white backdrop-blur-sm text-sm">
          <HelpCircle className="w-6 h-6 text-white hover:text-blue-300 transition-colors" />
        </Tooltip>
      </div>

      {!currentCompetition ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentLocation.competitions?.map((competition) => (
            <CompetitionCard
              key={competition.key}
              competition={competition}
              surferRating={surferRating}
              onEnroll={handleEnroll}
              disabled={gameData.currencies.money.amount < competition.entryFee}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 shadow-lg text-white">
          <h3 className="text-xl font-semibold mb-4">{currentCompetition.name}</h3>

          {currentRound && currentHeat && !gdcc?.isComplete && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center">
                  <Trophy className="w-5 h-5 mr-2" />
                  <span>Current Round: {formatUnderscoreCase(currentRound.key)}</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>
                    Heat {gdcc?.currentHeatIndex + 1} of {currentRound.heats.length}
                  </span>
                </div>
              </div>

              <CompetitionRoundsView
                rounds={gdcc.rounds}
                currentRoundIndex={gdcc.currentRoundIndex}
                currentHeatIndex={gdcc.currentHeatIndex}
              />

              {isPlayerInHeat && (
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-xl inline-flex items-center justify-center w-full"
                  onClick={handleStartHeat}
                >
                  Start Heat <ChevronRight className="w-6 h-6 ml-2" />
                </button>
              )}
            </div>
          )}

          {/* Competition Results */}
          {gdcc?.isComplete && (
            <div className="space-y-6">
              <Tabs
                tabs={[
                  { value: 'leaderboard', label: 'Leaderboard' },
                  { value: 'heats', label: 'All Heats' }
                ]}
                activeTab={resultsTab}
                onChange={setResultsTab}
              />

              {resultsTab === 'leaderboard' && (
                <div>
                  <h4 className="text-lg font-semibold mb-4">Final Results</h4>
                  <div className="space-y-2">
                    {leaderboard
                      .slice((gdcc?.currentPage - 1) * itemsPerPage, gdcc?.currentPage * itemsPerPage)
                      .map((entry, index) => (
                        <div
                          key={index}
                          className={`p-2 rounded-lg ${
                            entry.name === gameData.username ? 'bg-blue-500' : 'bg-blue-500/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-bold">
                                <span className="font-bold text-2xl">
                                  {entry.position === 1 && ' 🥇'}
                                  {entry.position === 2 && ' 🥈'}
                                  {entry.position === 3 && ' 🥉'}
                                </span>
                                {entry.position}
                                {getOrdinalSuffix(entry.position ?? 0)}:
                              </span>{' '}
                              {entry.name === gameData.username ? 'You' : entry.name}
                              <span className="text-sm text-white/60 ml-2">
                                ({entry.position && entry.position > 1 ? 'Eliminated in' : 'Won'}{' '}
                                {formatUnderscoreCase(entry.eliminatedInRound ?? '')}, Heat{' '}
                                {entry.eliminatedInHeat ?? 1})
                              </span>
                            </div>
                            {entry.prize && <div className="text-sm">Prize: ${formatCurrency(entry.prize)}</div>}
                          </div>
                        </div>
                      ))}
                  </div>
                  {leaderboard.length > itemsPerPage && (
                    <div className="flex justify-center mt-4 gap-2">
                      <button
                        className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                        onClick={() =>
                          setGameData((prev) => ({
                            ...prev,
                            currentCompetition: {
                              ...prev.currentCompetition!,
                              currentPage: Math.max(1, prev.currentCompetition!.currentPage - 1)
                            }
                          }))
                        }
                        disabled={gdcc?.currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1">
                        Page {gdcc?.currentPage} of {Math.ceil(leaderboard.length / itemsPerPage)}
                      </span>
                      <button
                        className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                        onClick={() =>
                          setGameData((prev) => ({
                            ...prev,
                            currentCompetition: {
                              ...prev.currentCompetition!,
                              currentPage: Math.min(
                                Math.ceil(leaderboard.length / itemsPerPage),
                                prev.currentCompetition!.currentPage + 1
                              )
                            }
                          }))
                        }
                        disabled={gdcc?.currentPage === Math.ceil(leaderboard.length / itemsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}

              {resultsTab === 'heats' && (
                <CompetitionRoundsView
                  rounds={gdcc.rounds}
                  currentRoundIndex={gdcc.currentRoundIndex}
                  currentHeatIndex={gdcc.currentHeatIndex}
                  isComplete={true}
                />
              )}

              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg text-xl inline-flex items-center justify-center w-full"
                onClick={handleClose}
              >
                Close Results
              </button>
            </div>
          )}
        </div>
      )}
      <Toaster richColors position="bottom-center" />
    </div>
  )
}
